import { SETTINGS_PATHS } from "@/features/settings/paths"
import SettingsApp from "@/features/settings/SettingsApp"
import { updateAuthStatus } from "@/services/api/authRequestLifecycle"
import { ThemeProvider, useAuthenticator } from "@aws-amplify/ui-react"
import * as Sentry from "@sentry/react"
import { fetchUserAttributes } from "aws-amplify/auth"
import React, { useCallback, useEffect, useState } from "react"
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router"
import { AutoTrackPageViews } from "./components/common/AutoTrackPageViews"
import {
  IndexRoute,
  RequireDataRoomActive,
  RequireDataRoomInactive,
} from "./components/common/dataRoomGates"
import { ErrorBoundary } from "./components/common/ErrorBoundary/ErrorBoundary"
import { InfoCircleMenu } from "./components/common/InfoCircleMenu/InfoCircleMenu"
import { LoadingSpinner } from "./components/common/LoadingSpinner/LoadingSpinner"
import { RequirePermission } from "./components/common/RequirePermission"
import { Authentication } from "./components/feature/Authenticaton/Authentication"
import { CustomTheme } from "./components/feature/CustomSignIn/CustomSignIn"
import { FeedbackModal } from "./components/feature/Modals/FeedbackModal/FeedbackModal"
import { UpgradeModal } from "./components/feature/Modals/UpgradeModal/UpgradeModal"
import { UpgradeNavigationListener } from "./components/feature/UpgradeSessionSync/UpgradeNavigationListener"
import { UpgradeSessionSync } from "./components/feature/UpgradeSessionSync/UpgradeSessionSync"
import { AppShellRoute } from "./components/layout/AppShellRoute"
import { Toaster } from "./components/ui/sooner"
import { PERMISSIONS } from "./constants/permissions"
import { AgentChatProvider } from "./contexts/AgentChatContext"
import { ChatProvider } from "./contexts/ChatContext"
import {
  FeatureFlagProvider,
  useFeatureFlag,
} from "./contexts/FeatureFlagContext"
import { RateLimitProvider } from "./contexts/RateLimitContext"
import { AgentPageContextProvider } from "./features/agent-chat/context/AgentPageContext"
import { NotificationCenterPage } from "./features/notifications"
import { OnboardingProvider } from "./features/onboarding"
import { UpgradePage } from "./features/upgrade/UpgradePage"
import { UpgradeReturnPage } from "./features/upgrade/UpgradeReturnPage"
import useUser from "./hooks/useUser"
import ChatHistoryPage from "./pages/ChatHistoryPage"
import Chat from "./pages/ChatPage"
import { CompanyInfoPage } from "./pages/CompanyInfoPage"
import { DataRoomPage } from "./pages/DataRoomPage"
import { ErrorPage } from "./pages/ErrorPage"
import Knowledgebase from "./pages/KnowledgebasePage"
import MaterialCompliancePage from "./pages/MaterialCompliancePage"
import { OnboardingPage } from "./pages/OnboardingPage"
import QmsIngestionPage from "./pages/QmsIngestionPage"
import { AnswersPage } from "./pages/questionnaire/AnswersPage"
import { ExcelAnswersPage } from "./pages/questionnaire/ExcelAnswersPage"
import { ExcelQuestionsPage } from "./pages/questionnaire/ExcelQuestionsPage/ExcelQuestionsPage"
import { GeneratingAnswersPage } from "./pages/questionnaire/GeneratingAnswersPage"
import { PdfAnswersPage } from "./pages/questionnaire/PdfAnswesPage/PdfAnswersPage"
import { PdfQuestionsPage } from "./pages/questionnaire/PdfQuestionsPage/PdfQuestionsPage"
import { PreviewPage } from "./pages/questionnaire/PreviewPage"
import { QuestionnaireProcessingPage } from "./pages/questionnaire/QuestionnaireProcessingPage"
import { QuestionsPage } from "./pages/questionnaire/QuestionsPage"
import { UploadPage } from "./pages/questionnaire/UploadPage"
import QuestionnaireHistoryPage from "./pages/QuestionnaireHistoryPage"
import TermsAndConditions from "./pages/TermsAndConditions"
const HelpdeskPage = React.lazy(() => import("./pages/HelpdeskPage"))
// Tiptap + the print stylesheet only load when someone opens the editor.
const DeclarationTemplateEditorPage = React.lazy(
  () => import("./pages/DeclarationTemplateEditorPage"),
)
// TEMPORARY: the ExcelEditor playground; delete together with the page.
// Development builds only: production builds drop the route and its chunk.
const ExcelPreviewPage = import.meta.env.DEV
  ? React.lazy(() => import("./pages/ExcelPreviewPage"))
  : null

const SentryRoutes = Sentry.wrapReactRouterRouting(Routes)

const ChatHistoryRoute: React.FC = () => {
  const chatHistoryEnabled = useFeatureFlag("CHAT_HISTORY")
  return chatHistoryEnabled ? (
    <ChatHistoryPage />
  ) : (
    <ErrorPage variant="feature-unavailable" withLayout />
  )
}

const MaterialComplianceRoute: React.FC = () => {
  const materialComplianceEnabled = useFeatureFlag("MATERIAL_COMPLIANCE")
  return materialComplianceEnabled ? (
    <MaterialCompliancePage />
  ) : (
    <ErrorPage variant="feature-unavailable" withLayout />
  )
}

const NotificationCenterRoute: React.FC = () => {
  const notificationsEnabled = useFeatureFlag("NOTIFICATIONS_IN_APP")
  return notificationsEnabled ? (
    <NotificationCenterPage />
  ) : (
    <ErrorPage variant="feature-unavailable" withLayout />
  )
}

const CompanyInfoRoute: React.FC = () => {
  const companyProfileEnabled = useFeatureFlag("COMPANY_PROFILE")
  return companyProfileEnabled ? (
    <CompanyInfoPage />
  ) : (
    <ErrorPage variant="feature-unavailable" withLayout />
  )
}

// Renders the info/help trigger. Feature-flagged: SIDEBAR_V2 moves this into
// the sidebar footer (AppSidebarV2 renders InfoCircleMenu variant="sidebar"),
// so with the flag on this component renders nothing here.
// The app shell stays mounted across navigation, so a lazy route's content
// pane needs its own loading state; a null fallback would leave it empty.
const lazyRouteFallback = (
  <div className="flex h-full w-full items-center justify-center">
    <LoadingSpinner />
  </div>
)

const HelpdeskRoute: React.FC = () => {
  const helpdeskEnabled = useFeatureFlag("HELPDESK")
  return helpdeskEnabled ? (
    <React.Suspense fallback={lazyRouteFallback}>
      <HelpdeskPage />
    </React.Suspense>
  ) : (
    <ErrorPage variant="feature-unavailable" withLayout />
  )
}

// The legacy three-video onboarding flow. Nothing links here any more, but the
// route stays reachable by URL, so it gets its own flag.
const OnboardingRoute: React.FC = () => {
  const onboardingVideoTourEnabled = useFeatureFlag("ONBOARDING_VIDEO_TOUR")
  return onboardingVideoTourEnabled ? (
    <OnboardingPage />
  ) : (
    <ErrorPage variant="feature-unavailable" withLayout />
  )
}

const InfoButtonWithPopover: React.FC = () => {
  const sidebarV2Enabled = useFeatureFlag("SIDEBAR_V2")
  if (sidebarV2Enabled) return null
  return <InfoCircleMenu variant="floating" />
}

const App: React.FC = () => {
  const { user, authStatus } = useAuthenticator((context) => [
    context.authStatus,
  ])
  useEffect(() => {
    updateAuthStatus(authStatus)
  }, [authStatus])
  // Invoked for its side effects (Sentry/amplitude user context, user data load).
  useUser()
  const [termsAccepted, setTermsAccepted] = useState(true)

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    let documentTitle = "turnus.ai"
    if (
      import.meta.env.VITE_ENVIRONMENT !== undefined &&
      import.meta.env.VITE_ENVIRONMENT !== "prod"
    ) {
      documentTitle = `[${import.meta.env.VITE_ENVIRONMENT}] ${documentTitle}`
    }
    document.title = documentTitle
  }, [])

  useCallback(() => {
    const checkTermsAcceptance = async () => {
      if (user) {
        const userAttributes = await fetchUserAttributes()
        const termsAccepted = userAttributes["custom:tcAcknowledged"] === "true"
        setTermsAccepted(termsAccepted)

        if (!termsAccepted && location.pathname !== "/terms") {
          navigate("/terms")
        }
      }
    }

    checkTermsAcceptance()
  }, [user, location.pathname, navigate])

  return (
    <ThemeProvider theme={CustomTheme}>
      <Authentication>
        <FeatureFlagProvider>
          <OnboardingProvider>
            <div className="flex h-screen max-h-screen bg-grey2 text-white">
              {termsAccepted ? (
                <ChatProvider>
                  <RateLimitProvider>
                    <AgentPageContextProvider>
                      <AgentChatProvider>
                        <AutoTrackPageViews />
                        <FeedbackModal />
                        <UpgradeModal />
                        <UpgradeSessionSync />
                        <UpgradeNavigationListener />
                        <InfoButtonWithPopover />
                        <div className="h-screen w-screen">
                          <ErrorBoundary
                            fallback={<ErrorPage variant="error" />}
                            resetKey={location.pathname}
                            onError={(error, errorInfo) =>
                              Sentry.captureException(error, {
                                extra: {
                                  componentStack: errorInfo.componentStack,
                                },
                              })
                            }
                          >
                            <SentryRoutes>
                              {/* Layout route: the app shell mounts once for every page. */}
                              <Route element={<AppShellRoute />}>
                                <Route index element={<IndexRoute />} />
                                <Route
                                  path="data-room"
                                  element={
                                    <RequireDataRoomActive>
                                      <DataRoomPage />
                                    </RequireDataRoomActive>
                                  }
                                />
                                <Route
                                  path="questionnaire/upload"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <UploadPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="questionnaire/:questionnaireId/questions"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <QuestionsPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="questionnaire/:questionnaireId/processing"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <QuestionnaireProcessingPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="questionnaire/:questionnaireId/excel-questions"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <ExcelQuestionsPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="questionnaire/:questionnaireId/pdf-questions"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <PdfQuestionsPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="questionnaire/:questionnaireId/auto-fill/:taskGroupId"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <GeneratingAnswersPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="questionnaire/:questionnaireId/answers"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <AnswersPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="questionnaire/:questionnaireId/excel-answers"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <ExcelAnswersPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="questionnaire/:questionnaireId/pdf-answers"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <PdfAnswersPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="questionnaire/:questionnaireId/preview"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <PreviewPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="knowledgebase"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.KB_VIEW}
                                      >
                                        <Knowledgebase />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="qms-database/*"
                                  element={
                                    <RequireDataRoomInactive>
                                      <QmsIngestionPage />
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="material-compliance/*"
                                  element={
                                    <RequireDataRoomInactive>
                                      <MaterialComplianceRoute />
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="company-info"
                                  element={
                                    <RequireDataRoomInactive>
                                      <CompanyInfoRoute />
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="chat"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.CHAT_USE}
                                      >
                                        <Chat />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="chat/history"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.CHAT_USE}
                                      >
                                        <ChatHistoryRoute />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="history"
                                  element={
                                    <RequireDataRoomInactive>
                                      <RequirePermission
                                        permission={PERMISSIONS.QUEST_VIEW}
                                      >
                                        <QuestionnaireHistoryPage />
                                      </RequirePermission>
                                    </RequireDataRoomInactive>
                                  }
                                />
                                <Route
                                  path="help/*"
                                  element={<HelpdeskRoute />}
                                />
                                <Route
                                  path="notifications"
                                  element={
                                    <RequireDataRoomInactive>
                                      <NotificationCenterRoute />
                                    </RequireDataRoomInactive>
                                  }
                                />
                                {/* The old page; notification emails sent
                                    before it moved still link here. */}
                                <Route
                                  path="notifications/settings"
                                  element={
                                    <Navigate
                                      to={SETTINGS_PATHS.notifications}
                                      replace
                                    />
                                  }
                                />
                                <Route
                                  path="onboarding"
                                  element={<OnboardingRoute />}
                                />
                                {/* Settings is available to all plans so org admins can
                              reach team management (not just free-trial users). */}
                                <Route
                                  path="settings/*"
                                  element={<SettingsApp />}
                                />
                                <Route
                                  path="settings/declaration-templates/:key"
                                  element={
                                    <React.Suspense
                                      fallback={lazyRouteFallback}
                                    >
                                      <DeclarationTemplateEditorPage />
                                    </React.Suspense>
                                  }
                                />
                                {ExcelPreviewPage ? (
                                  <Route
                                    path="dev/excel-preview"
                                    element={
                                      <React.Suspense
                                        fallback={lazyRouteFallback}
                                      >
                                        <ExcelPreviewPage />
                                      </React.Suspense>
                                    }
                                  />
                                ) : null}
                                <Route
                                  path="upgrade"
                                  element={<UpgradePage />}
                                />
                                <Route
                                  path="upgrade/return"
                                  element={<UpgradeReturnPage />}
                                />
                                <Route
                                  path="*"
                                  element={
                                    <ErrorPage withLayout variant="not-found" />
                                  }
                                />
                              </Route>
                            </SentryRoutes>
                          </ErrorBoundary>
                        </div>
                        <Toaster
                          position="top-right"
                          toastOptions={{
                            duration: 3500,
                          }}
                        />
                      </AgentChatProvider>
                    </AgentPageContextProvider>
                  </RateLimitProvider>
                </ChatProvider>
              ) : (
                <TermsAndConditions setTermsAccepted={setTermsAccepted} />
              )}
            </div>
          </OnboardingProvider>
        </FeatureFlagProvider>
      </Authentication>
    </ThemeProvider>
  )
}

export default App
